import { useCallback } from 'react';
import { MediaType, useCustomMutation } from '../../graphql';
import { useGrippContext } from '../../grippContext';

export interface RecordMediaViewInput {
  workspaceAssetId: string;
  mediaId: string;
  mediaType: MediaType;
}

/**
 * Media types counted as a deliberate "file open" for activity reporting —
 * documents, videos and links. Image gallery views and audio are intentionally
 * excluded (noisy / not a discrete open). Shared by the web and mobile
 * file-open screens so the two stay in lockstep.
 */
export const TRACKED_MEDIA_VIEW_TYPES: MediaType[] = [
  MediaType.Document,
  MediaType.Video,
  MediaType.Link,
];

const RECORD_MEDIA_VIEW = `
  mutation RecordMediaView($input: RecordMediaViewInput!) {
    media {
      recordMediaView(input: $input) {
        success
      }
    }
  }
`;

/**
 * Fire-and-forget recording of a file open (document / video / link) for
 * activity reporting. The returned function never rejects — failures are
 * swallowed and logged, since telemetry must not surface to the user or break
 * the file-open flow.
 */
export const useRecordMediaView = () => {
  const { mutateAsync } = useCustomMutation({ query: RECORD_MEDIA_VIEW });
  const { logger } = useGrippContext();

  return useCallback(
    async (input: RecordMediaViewInput): Promise<void> => {
      try {
        await mutateAsync({ input });
      } catch (error) {
        logger.error(error, 'Failed to record media view');
      }
    },
    [mutateAsync, logger]
  );
};
